import React, { useMemo, useCallback, forwardRef } from 'react';
import { MultiSelectInput, type MultiSelectOption } from './MultiSelectInput';
import { cn } from '../../utils/cn';
import type { HealthCondition } from '../../types/referral';
import { HEALTH_CONDITIONS } from '../../types/referral';

interface AreasOfPracticeSelectorProps {
  selectedConditions: HealthCondition[];
  onConditionsChange: (conditions: HealthCondition[]) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * AreasOfPracticeSelector Component
 *
 * Multi-select interface for areas of practice using the MultiSelectInput component.
 * Follows BiteWise accessibility and design patterns.
 */
export const AreasOfPracticeSelector = forwardRef<HTMLDivElement, AreasOfPracticeSelectorProps>(({
  selectedConditions,
  onConditionsChange,
  error,
  disabled = false,
  className,
}, ref) => {
  // Convert HEALTH_CONDITIONS to MultiSelectOption format
  const healthConditionOptions = useMemo((): MultiSelectOption[] => {
    return HEALTH_CONDITIONS.map(condition => ({
      value: condition,
      label: condition,
      disabled: false
    }));
  }, []);

  // Handle selection change from MultiSelectInput
  const handleSelectionChange = useCallback((selectedValues: string[]) => {
    // Convert string[] back to HealthCondition[]
    const selectedHealthConditions = selectedValues as HealthCondition[];
    onConditionsChange(selectedHealthConditions);
  }, [onConditionsChange]);

  return (
    <div ref={ref} className={cn(className)}>
      <MultiSelectInput
        options={healthConditionOptions}
        value={selectedConditions}
        onChange={handleSelectionChange}
        placeholder="Select areas of practice..."
        dialogTitle="Select Areas of Practice"
        searchPlaceholder="Search areas of practice..."
        searchable={true}
        maxVisibleCapsules={3}
        state={error ? 'error' : 'default'}
        error={error}
        disabled={disabled}
        helperText="Select all areas of practice that apply"
        fullWidth
        containerClassName="space-y-1"
        suppressMessage={true}
      />
    </div>
  );
});

AreasOfPracticeSelector.displayName = 'AreasOfPracticeSelector';

export default AreasOfPracticeSelector;